import { Location } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { GurdServiceService } from 'src/app/Service/gurd-service.service';
import { NotificationService } from 'src/app/Service/notification.service';
import { OtherService } from 'src/app/Service/other.service';
import { ReportExportService } from '../../report-export.service';
import { ReportContext, ReportDefinition } from '../../report.models';
import { toEnglishDigits } from '../../report.utils';
import { StopTankerReportApiService } from './stop-tanker-report-api.service';
import { StopTankerReportColumn, StopTankerReportRow } from './stop-tanker-report.models';
import { buildStopTankerSearchIndex, getStopTankerColumns, getStopTankerExportColumns, getStopTankerTotal, STOP_TANKER_PAGE_SIZE, STOP_TANKER_PAGE_SIZE_OPTIONS } from './stop-tanker-report.utils';

@Component({
  selector: 'app-stop-tanker-report',
  templateUrl: './stop-tanker-report.component.html',
})
export class StopTankerReportComponent implements OnChanges, OnDestroy {
  @Input() context!: ReportContext;
  @Input() definition!: ReportDefinition;

  columns: StopTankerReportColumn[] = [];
  rows: StopTankerReportRow[] = [];
  filteredRows: StopTankerReportRow[] = [];
  pagedRows: StopTankerReportRow[] = [];
  searchText = '';
  currentPage = 1;
  pageSize = STOP_TANKER_PAGE_SIZE;
  readonly pageSizeOptions = STOP_TANKER_PAGE_SIZE_OPTIONS;
  totalPages = 1;
  loading = false;
  hasError = false;
  private requestVersion = 0;
  private destroyed = false;

  constructor(
    private readonly api: StopTankerReportApiService,
    private readonly exportService: ReportExportService,
    private readonly gurdService: GurdServiceService,
    private readonly notificationService: NotificationService,
    private readonly otherService: OtherService,
    private readonly router: Router,
    private readonly location: Location,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ((!changes['context'] && !changes['definition']) || !this.context || !this.definition?.stopTankerMode) return;
    this.requestVersion++;
    this.loading = false;
    this.columns = getStopTankerColumns(this.definition.stopTankerMode);
    this.rows = [];
    this.searchText = '';
    this.hasError = false;
    this.refreshTable(true);
    void this.loadReport();
  }
  ngOnDestroy(): void { this.destroyed = true; ++this.requestVersion; }

  async loadReport(): Promise<void> {
    if (this.loading || this.destroyed || !this.definition.endpoint) return;
    const version = ++this.requestVersion;
    this.loading = true;
    this.hasError = false;
    try {
      const result = await this.api.loadReport(this.definition, this.context, this.credentials);
      if (version !== this.requestVersion) return;
      if (!Array.isArray(result)) {
        if (result.ISSessionExpired || result.ISessionExpired) {
          this.notificationService.showError('لطفا وارد سیستم شوید');
          this.otherService.removeLocalStorage();
          void this.router.navigate(['login'], { queryParams: { report: this.definition.loginReport || this.context.id } });
          return;
        }
        throw new Error(result.strError || 'دریافت اطلاعات با خطا مواجه شد');
      }
      this.rows = result.map((row) => ({ ...row, searchIndex: buildStopTankerSearchIndex(row) }));
      this.refreshTable(true);
    } catch (error: unknown) {
      if (version !== this.requestVersion) return;
      this.rows = [];
      this.hasError = true;
      this.refreshTable(true);
      this.notificationService.showError(error instanceof Error ? error.message : 'دریافت اطلاعات با خطا مواجه شد');
    } finally {
      if (version === this.requestVersion) this.loading = false;
    }
  }

  onSearchChange(): void { if (!this.loading) this.refreshTable(true); }
  clearSearch(): void { if (!this.loading) { this.searchText = ''; this.refreshTable(true); } }
  onPageSizeChange(pageSize: number): void { if (!this.loading && pageSize > 0) { this.pageSize = pageSize; this.refreshTable(true); } }
  goToPage(page: number): void { if (!this.loading && page >= 1 && page <= this.totalPages && page !== this.currentPage) { this.currentPage = page; this.refreshTable(); } }
  goBack(): void { this.location.back(); }

  exportExcel(): void {
    if (this.loading || this.hasError || !this.filteredRows.length) return;
    const columns = getStopTankerExportColumns(this.definition.stopTankerMode!);
    const rows: (string | number)[][] = this.filteredRows.map((row, index) => [index + 1, ...columns.map((column) => this.cellValue(row, column))]);
    if (this.hasRefuelingRowAction) rows.push([`تعداد کل: ${this.filteredRows.length}`, ...columns.map(column => column.total ? this.totalValue(column) : '')]);
    this.exportService.exportRows(this.definition.fileName, ['#', ...columns.map((column) => column.title)], rows);
  }

  openAllOnMap(): void {
    if (this.loading || this.hasError || !this.filteredRows.length || !this.hasMapAction) return;
    const suffix = this.hasRefuelingRowAction ? '÷' : this.definition.stopTankerMode === 'stop' ? '$'
      : this.definition.stopTankerMode === 'tanker-discharge' ? '&'
        : this.definition.stopTankerMode === 'tanker-refueling' ? '|'
          : '=';
    void this.router.navigate([`/stopMap/${this.context.id}${suffix}`]);
  }
  openRefuelingLocation(row: StopTankerReportRow): void {
    if (this.loading || this.hasError || !this.hasRefuelingRowAction) return;
    const values = { fLat: row.fLat ?? '', fLon: row.fLon ?? '', commentJournal: row.strFullName ?? '',
      timeJournal: row.strTime ?? '', dateJournal: row.strDate ?? '', fule: row.fLiter ?? '', IDefKM: row.IDefKM ?? '' };
    for (const [key, value] of Object.entries(values)) this.storeEncrypted(key, value);
    void this.router.navigate(['stopMap/2']);
  }

  openStopLocation(row: StopTankerReportRow): void {
    if (this.loading || this.hasError || !this.hasStopRowAction) return;
    this.storeEncrypted('StopModel', JSON.stringify({ strDate: row.strDate, strAddress: row.strAddress, strSTime: row.strSTime, strETime: row.strETime, strParkTime: row.strParkTime }));
    this.storeEncrypted('fLat', row.fLat ?? '');
    this.storeEncrypted('fLon', row.fLon ?? '');
    void this.router.navigate(['stopMap']);
  }

  openDischargeRoute(row: StopTankerReportRow): void {
    if (this.loading || this.hasError || !this.hasDischargeRowAction) return;
    this.storeEncrypted('RouteDate', row.strDate ?? '');
    this.storeEncrypted('reportStime', row.strSTime ?? '');
    this.storeEncrypted('reportEtime', row.strETime ?? '');
    const url = this.router.serializeUrl(this.router.createUrlTree([`route/${row.strUnitID || this.context.id}`]));
    window.open(url, '_blank');
  }

  cellValue(row: StopTankerReportRow, column: StopTankerReportColumn): string | number {
    const value = row[column.field];
    return column.format === 'number' ? Number(value) || 0 : String(value ?? '');
  }

  totalValue(column: StopTankerReportColumn): number { return getStopTankerTotal(this.filteredRows, column.field); }
  get hasActiveFilters(): boolean { return !!this.searchText.trim(); }
  get subtitle(): string { return `از تاریخ ${this.context.startDate} الی ${this.context.endDate}`; }
  get searchPlaceholder(): string { return `جستجو در جدول: ${(this.definition.searchFields || []).join('، ')}...`; }
  get tableId(): string { return `stop-tanker-report-${this.definition.stopTankerMode}`; }
  get printSectionId(): string { return `stop-tanker-report-print-${this.definition.stopTankerMode}`; }
  get hasMapAction(): boolean { return this.definition.stopTankerMode !== 'tanker-feeding'; }
  get hasStopRowAction(): boolean { return ['stop', 'tanker-refueling', 'tanker-suspicious-discharge'].includes(this.definition.stopTankerMode!); }
  get hasDischargeRowAction(): boolean { return this.definition.stopTankerMode === 'tanker-discharge'; }
  get hasRefuelingRowAction(): boolean { return this.definition.stopTankerMode === 'vehicle-refueling'; }
  get hasRowActions(): boolean { return this.hasStopRowAction || this.hasDischargeRowAction || this.hasRefuelingRowAction; }

  private get credentials() { return { iSystemUser: this.gurdService.decrypt(localStorage.getItem('ID')!), strSession: this.gurdService.decrypt(localStorage.getItem('Session')!) }; }
  private storeEncrypted(key: string, value: unknown): void { localStorage.setItem(key, this.gurdService.encrypt(String(value))); }
  private refreshTable(resetPage = false): void {
    if (resetPage) this.currentPage = 1;
    const query = toEnglishDigits(this.searchText).trim().toLowerCase();
    this.filteredRows = query ? this.rows.filter((row) => row.searchIndex?.includes(query)) : this.rows;
    this.totalPages = Math.max(1, Math.ceil(this.filteredRows.length / this.pageSize));
    this.currentPage = Math.min(this.currentPage, this.totalPages);
    const offset = (this.currentPage - 1) * this.pageSize;
    this.pagedRows = this.filteredRows.slice(offset, offset + this.pageSize);
  }
}
